import React, { useState, useEffect, useContext, useRef } from 'react';
import {Select, DatePicker, Icon, Spin, Modal, Button, Tooltip} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import uuidv4 from 'uuid/v4';
import find from 'lodash/find';
import {
  dateToString,
  stringToDate,
  MULTI_DATE_FORMATS,
} from '../../../utils/dateUtils';
import { ModelContext } from '../../metadata/ModelSource';
import { FormContext } from '../../metadata/MDForm';
import { GlobalStateContext } from '../../../context/GlobalStateProvider';
import findIndex from 'lodash/findIndex';

const { Option } = Select;

const isOdd = (number) => {
  const value = number % 2;
  if (value === 0) {
    return false;
  } else {
    return true;
  }
};

const InfractionsMobile = ({ driverInfractionCds }) => {
  const [data, setData] = useState([]);
  const [editingKey, setEditingKey] = useState('');
  const occurrenceDateRef = useRef(null);
  const infractionCodeRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const applicationModel = useContext(ModelContext);
  const formContext = useContext(FormContext);
  const { changePaymentLayout: changeInfractionLayout } = useContext(
      GlobalStateContext
  );
  const { prefix } = formContext;
  const prefixPath = `${prefix}driverInfo.driverPoints`;
  let container = applicationModel.getValue(prefixPath);

  useEffect(() => {
    if (container === undefined) {
      container = [];
      applicationModel.setValue(prefixPath, container);
    }
    let activeDriverPoints = container.length > 0 ? container.filter((driverPoint) => driverPoint.status === 'Active') : [];
    if(activeDriverPoints.length){    //if there are driverPoints on container add them to the form otherwise add an empty driver point to form
      const newData = [...data];
      activeDriverPoints.forEach(driverPoint => {
        const {infractionCd} = driverPoint;
        const infractionDesc = getInfractionDescription(infractionCd);
        newData.push({...driverPoint, infractionCd: infractionDesc});
      });
      setData(newData);
    }else{
      addInfraction()
    }
  }, []);

  const addInfraction = () => {
    const tempId = uuidv4();

    const newData = [...data];

    newData.push({
      infractionDt: (
          <DatePicker aria-label="Select infraction date" format={MULTI_DATE_FORMATS} onChange={handleDateSelect}/>
      ),
      infractionCd: <InfractionSelect aria-label="Select infraction"/>,
      id: tempId,
    });
    setData(newData);
    setEditingKey(tempId);
    setDisabled(true);

  };

  const editInfraction = (record) => {
    let container = applicationModel.getValue(prefixPath);
    const formItem = find(container,{id: record.id});
    const newData = [...data];

    const editableData = {
      ...record,
      infractionDt: (
          <DatePicker
              aria-label="Select infraction date"
              format={MULTI_DATE_FORMATS}
              defaultValue={stringToDate(formItem.infractionDt)}
              onChange={handleDateSelect}
          />
      ),
      infractionCd: <InfractionSelect aria-label="Select infraction" previousValue={formItem.infractionCd} />,
    };

    setEditingKey(record.id);
    setDisabled(true);

    const index = findIndex(newData,{id: record.id});
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, {
        ...item,
        ...editableData,
      });
      setData(newData);
    }
  };

  const saveInfraction = (record) => {
    setLoading(true);
    setDisabled(true);
    const newData = [...data];
    let container = applicationModel.getValue(prefixPath);
    const dataIndex = findIndex(newData,{id: record.id});
    const item = newData[dataIndex];
    const containerIndex = findIndex(container,{id: record.id});
    const updatedInfraction = find(driverInfractionCds, {
      infractionCd:  infractionCodeRef.current,
    });

    let formItem;
    if(containerIndex > -1) {
      formItem = container[containerIndex];
      container.splice(containerIndex, 1, {
        ...formItem,
        ...record,
        infractionDt:
            occurrenceDateRef.current !== null
                ? occurrenceDateRef.current
                : formItem.infractionDt,
        infractionCd:
            updatedInfraction !== undefined
                ? updatedInfraction.infractionCd
                : formItem.infractionCd,
        pointsCharged:  updatedInfraction !== undefined ? updatedInfraction.points : formItem.pointsCharged,
        status: 'Active',
      });
    }else{
      container.push({
        ...record,
        infractionDt: occurrenceDateRef.current,
        infractionCd: updatedInfraction.infractionCd,
        pointsCharged:  updatedInfraction !== undefined ? updatedInfraction.points : formItem.pointsCharged,
        status: 'Active',
      });
    }

    newData.splice(dataIndex, 1, {
      ...item,
      ...record,
      infractionDt:
          occurrenceDateRef.current !== null
              ? occurrenceDateRef.current
              : formItem.infractionDt,
      infractionCd:
          updatedInfraction !== undefined
              ? updatedInfraction.description
              : getInfractionDescription(formItem.infractionCd),
      pointsCharged:  updatedInfraction !== undefined ? updatedInfraction.points : formItem.pointsCharged,
      status: 'Active',
    });

    setLoading(false);
    setDisabled(false);
    setData(newData);
    occurrenceDateRef.current = null;
    infractionCodeRef.current = null;
    setEditingKey('');
    applicationModel.modification();

  };

  const deleteInfraction = () => {
    //if the infraction is saved, it means it's on the container, set status to deleted on the container and delete it from the form
    //otherwise infraction hasn't been saved just delete it from the form

    const newData = [...data];
    let container = applicationModel.getValue(prefixPath);
    const containerIndex = findIndex(container,{id: editingKey});
    const formIndex = findIndex(newData,{id: editingKey});
    setLoading(true);

    if(containerIndex > -1) { //if infraction is saved already which means it's on the container, set status to delete on the container and delete it from newData
      container.splice(containerIndex, 1, {
        ...container[containerIndex],
        status: 'Deleted',
      });
    }

    newData.splice(formIndex,1);


      setLoading(false);
      setEditingKey('');
      setData(newData);
      setModalVisible(false);
      applicationModel.modification();
  };

  const cancelEdit = (record) => {
    //if canceling edit to an item that has not been saved to container yet just remove it from newData
    //if canceling edit to  an item in the container restore the text fields
    const newData = [...data];
    const containerItem = find(container,{id:record.id});

    if(containerItem === undefined){
      //canceling edit to an item that has not been saved to container yet just remove it from newData
      const itemIndex = findIndex(newData,{id:record.id});
      newData.splice(itemIndex, 1);
    }else{
      //if canceling edit to an item in the container restore the text fields
      const itemIndex = findIndex(newData,{id:record.id});
      newData.splice(itemIndex,1,{
        ...newData[itemIndex],
        infractionCd: getInfractionDescription(containerItem.infractionCd),
        infractionDt: containerItem.infractionDt
      })
    }
    setData(newData);
    setDisabled(false);
    setEditingKey('');
  };

  const handleDateSelect = (value) => {
    occurrenceDateRef.current = dateToString(value);
  };

  const handleSelect = (value) => {
    editingKey;
    infractionCodeRef.current = value;
  };

  const  getInfractionDescription = (infractionCd) => {
    return driverInfractionCds.find((el) => infractionCd === el.infractionCd).description;
  }

  const InfractionSelect = ({ previousValue }) => {
    return (
        <Select
            defaultValue={previousValue ? previousValue : ''}
            onChange={handleSelect}
            style={{ width: '200px' }}
            loading={loading}
            showSearch
            filterOption={(input, option) =>
                option.props.children.toLowerCase().startsWith(input.toLowerCase())
            }
        >
          {driverInfractionCds &&
          driverInfractionCds.map((infraction, index) => (
              <Option key={index} value={infraction.infractionCd}>
                {infraction.description}
              </Option>
          ))}
        </Select>
    );
  };

  const antIcon = <LoadingOutlined style={{ fontSize: '18px' }} spin />;

  const styles = {
    row: {
      display: 'flex',
      flexDirection: !changeInfractionLayout ? 'row' : 'column',
      justifyContent: !changeInfractionLayout ? 'space-between' : 'center',
      alignContent: 'center',
      alignItems: !changeInfractionLayout ? 'center' : 'flex-start',
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  };

  const InfractionButtons  = (dataItem) => {
    if (dataItem.id === editingKey) {
      return (
          <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignContent: 'center',
                width: '80px',
                paddingTop: '8px',
                paddingBottom: '8px',
              }}
          >
            <Tooltip title={'Save'}>
              <Button icon={'check'}
                      type={'primary'}
                      className={'save-infraction-btn'}
                      onClick={() => saveInfraction(dataItem)}
              />
            </Tooltip>
            <Tooltip title={'Cancel Edit'}>
              <Button className={'cancel-edit-infraction-btn'}
                      onClick={() => cancelEdit(dataItem)}
                      icon={'close'}
                      type={'secondary'}
              />
            </Tooltip>
          </div>
      );
    }
    return (
        <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '80px',
              paddingTop: '8px',
              paddingBottom: '8px',
            }}
        >
          <Tooltip title='Edit'>
            <Button disabled={disabled}
                    className={'edit-infraction-btn'}
                    onClick={() => editInfraction(dataItem)}
                    icon={'edit'}
                    type={'primary'}
            />
          </Tooltip>
          <Tooltip title='Delete'>
            <Button
                className={'delete-infraction-btn'}
                disabled={disabled}
                onClick={() => {
                  setModalVisible(true);
                  setEditingKey(dataItem.id);
                }}
                icon={'delete'}
                type={'secondary'}
            />
          </Tooltip>
        </div>
    );
  };

  const Table = ({ data }) => {
    return data.map((dataItem, index) => {
      const InfractionDate =  dataItem.infractionDt;
      const InfractionCode =dataItem.infractionCd;


      return (
          <div
              style={{
                backgroundColor: isOdd(index) ? '#f8f9fa' : '',
                padding: '8px',
              }}
              key={index}
          >
            {loading ? (
                <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '102px',
                    }}
                >
                  <Spin key={index} indicator={antIcon} />
                </div>
            ) : (
                <>
                  <div style={styles.row}>
                    <p style={{ fontWeight: 'bold', margin: 0 }}>Occurrence Date</p>
                    {InfractionDate }
                  </div>
                  <div style={styles.row}>
                    <p style={{ fontWeight: 'bold', margin: 0 }}>Infraction</p>
                    {InfractionCode }
                  </div>
                </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <InfractionButtons {...dataItem} />
            </div>
          </div>
      );
    });
  };

  return (
      <div>
        <Modal
            title="Infraction"
            visible={modalVisible}
            onCancel={() => setModalVisible(false)}
            footer={[
              <button
                  className="cancel-button"
                  key="back"
                  onClick={() => setModalVisible(false)}
              >
                Cancel
              </button>,
              <Button
                  loading={loading}
                  key="submit"
                  type="primary"
                  onClick={() => {
                    deleteInfraction();
                  }}
              >
                Remove
              </Button>,
            ]}
        >
          <p>Are you sure you want to remove this infraction?</p>
        </Modal>
        <Table data={data} />
        <Button type="link" disabled={disabled} onClick={() => addInfraction()}>
          Add More Infractions
        </Button>
      </div>
  );
};

export default InfractionsMobile;
